import type {ReactNode} from 'react';
import styled, {css} from 'styled-components';

// The shared empty-state container for the ticketing tree. It owns the generous
// centred padding that every "nothing here yet" block needs, so the values are
// stated once here instead of being repeated per tab.
//
// The padding below is deliberately off the design system's spacing scale. Empty
// states need far more breathing room than any scale step provides, and the
// alternative — inventing ticketing-private spacing tokens — is the private-token
// pattern this tree moved away from. See docs/spec/ticketing/frontend/sky-theme.md.
//
// Variants:
//   card — a raised bordered surface; the default for an empty tab body
//   inset — the same padding without chrome, for a block already inside a card
//   page — a narrow centred column for a whole-page empty or error state
export type EmptyStateVariant = 'card' | 'inset' | 'page';

const CARD_PADDING = '48px 24px';
const INSET_PADDING = '64px 0';
const PAGE_PADDING = '96px 0';

const cardChrome = css`
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	padding: ${CARD_PADDING};
`;

const insetChrome = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: ${INSET_PADDING};
`;

const pageChrome = css`
	margin: 0 auto;
	max-width: 28rem;
	padding: ${PAGE_PADDING};
`;

const Container = styled.div<{$variant: EmptyStateVariant}>`
	text-align: center;
	${p => p.$variant === 'card' && cardChrome}
	${p => p.$variant === 'inset' && insetChrome}
	${p => p.$variant === 'page' && pageChrome}
`;

export interface EmptyStateProps {
	children: ReactNode;
	/** Defaults to the bordered card used by empty tab bodies. */
	variant?: EmptyStateVariant;
	'data-testid'?: string;
}

export function EmptyState({children, variant = 'card', ...rest}: EmptyStateProps) {
	return (
		<Container $variant={variant} {...rest}>
			{children}
		</Container>
	);
}
